import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import { ClipboardList, FileSignature, MessageCircle } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

function formatDate(d) {
  return d ? new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : "-";
}

export default function AdminPendaftaran() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [actionModal, setActionModal] = useState(null);
  const [notes, setNotes] = useState("");
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ total_fee: "", due_date: "", method: "Transfer Bank Manual" });

  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    axios.get(`${API}/admin/pendaftaran`, { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };

  useEffect(() => { load(); }, [status]);

  const handleAction = async () => {
    try {
      await axios.put(`${API}/admin/pendaftaran/${actionModal.id}/${actionModal.action}`, { notes });
      toast.success("Tindakan berhasil dilakukan");
      setActionModal(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openInvoice = (r) => {
    setInvoiceModal(r);
    setInvoiceForm({ total_fee: "", due_date: "", method: "Transfer Bank Manual" });
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.total_fee || Number(invoiceForm.total_fee) <= 0) return toast.error("Mohon isi nominal tagihan");
    try {
      await axios.post(`${API}/admin/pembayaran`, {
        user_id: invoiceModal.user_id,
        program_id: invoiceModal.program_id,
        total_fee: Number(invoiceForm.total_fee),
        due_date: invoiceForm.due_date || null,
        method: invoiceForm.method,
      });
      toast.success("Tagihan berhasil dikirim ke jamaah");
      const rp = `Rp ${Number(invoiceForm.total_fee).toLocaleString("id-ID")}`;
      const message = `Assalamu'alaikum ${invoiceModal.name},\n\nTagihan pembayaran program Anda di KBIHU Muhammadiyah Surabaya telah tersedia:\nNominal: ${rp}${invoiceForm.due_date ? `\nJatuh Tempo: ${new Date(invoiceForm.due_date).toLocaleDateString("id-ID")}` : ""}\n\nSilakan login ke dashboard Anda untuk melihat detail rekening pembayaran dan mengunggah bukti transfer.\n\nTerima kasih.`;
      openWhatsApp(invoiceModal.phone, message);
      setInvoiceModal(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div data-testid="admin-pendaftaran-page" className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Antrian Pendaftaran</h1>
        <p className="text-sm text-[#64748B]">Verifikasi dan kelola pendaftaran calon jamaah baru.</p>
      </div>

      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="admin-pendaftaran-status-filter" className="h-10 w-full rounded-xl sm:w-52"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Status</SelectItem>
          <SelectItem value="pending">Menunggu</SelectItem>
          <SelectItem value="approved">Disetujui</SelectItem>
          <SelectItem value="rejected">Ditolak</SelectItem>
          <SelectItem value="correction">Perlu Perbaikan</SelectItem>
        </SelectContent>
      </Select>

      {items === null ? (
        <LoadingScreen />
      ) : items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Belum ada pendaftaran" description="Pendaftaran baru akan muncul di sini." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-pendaftaran-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>HP</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} data-testid={`admin-pendaftaran-row-${r.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{r.name}</TableCell>
                  <TableCell className="text-[#64748B]">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="capitalize text-[#64748B]">{r.program_type}</TableCell>
                  <TableCell className="text-[#64748B]">{r.phone}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right">
                    {r.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" data-testid={`admin-pendaftaran-approve-${r.id}`} onClick={() => setActionModal({ id: r.id, action: "approve" })} className="rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Setujui</Button>
                        <Button size="sm" variant="outline" data-testid={`admin-pendaftaran-correction-${r.id}`} onClick={() => setActionModal({ id: r.id, action: "correction" })} className="rounded-full">Perbaikan</Button>
                        <Button size="sm" variant="outline" data-testid={`admin-pendaftaran-reject-${r.id}`} onClick={() => setActionModal({ id: r.id, action: "reject" })} className="rounded-full border-red-200 text-red-600 hover:bg-red-50">Tolak</Button>
                      </div>
                    )}
                    {r.status === "approved" && !r.has_payment && (
                      <Button size="sm" data-testid={`admin-pendaftaran-buat-tagihan-${r.id}`} onClick={() => openInvoice(r)} className="gap-1.5 rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#F5B700] text-white hover:opacity-90">
                        <FileSignature className="h-3.5 w-3.5" /> Buat Tagihan
                      </Button>
                    )}
                    {r.status === "approved" && r.has_payment && (
                      <span className="text-xs text-[#64748B]">Tagihan terkirim</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!actionModal} onOpenChange={(v) => !v && setActionModal(null)}>
        <DialogContent data-testid="admin-pendaftaran-action-modal">
          <DialogHeader><DialogTitle>Tambahkan Catatan (Opsional)</DialogTitle></DialogHeader>
          <Textarea data-testid="admin-pendaftaran-action-notes" placeholder="Catatan untuk jamaah..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          <Button data-testid="admin-pendaftaran-action-confirm" onClick={handleAction} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Konfirmasi</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invoiceModal} onOpenChange={(v) => !v && setInvoiceModal(null)}>
        <DialogContent data-testid="admin-invoice-modal">
          <DialogHeader><DialogTitle>Kirim Tagihan ke {invoiceModal?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nominal Tagihan (Rp)</Label><Input data-testid="admin-invoice-total-fee" type="number" value={invoiceForm.total_fee} onChange={(e) => setInvoiceForm((f) => ({ ...f, total_fee: e.target.value }))} placeholder="Contoh: 32500000" /></div>
            <div><Label>Jatuh Tempo (Opsional)</Label><Input data-testid="admin-invoice-due-date" type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, due_date: e.target.value }))} /></div>
            <div><Label>Metode Pembayaran</Label><Input data-testid="admin-invoice-method" value={invoiceForm.method} onChange={(e) => setInvoiceForm((f) => ({ ...f, method: e.target.value }))} /></div>
            <Button data-testid="admin-invoice-submit-button" onClick={handleCreateInvoice} className="w-full gap-2 rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#F5B700] text-white hover:opacity-90"><MessageCircle className="h-4 w-4" /> Kirim Tagihan &amp; Notifikasi WhatsApp</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
